import React from 'react';
import clsx from 'clsx';

/**
 * Checkbox + Radio — canonical selection controls (REQ-SYS-CHK-01).
 *
 * The accent color is ALWAYS the brand action red (--color-action-primary),
 * never the platform default blue. This is the single source for every
 * checkbox/radio in the app — no inline `<input type="checkbox">` outside
 * this file (and BulkRowCheckbox, which already follows the same rule).
 *
 * States: default · checked · focus · disabled · (checkbox) indeterminate.
 *
 * Usage:
 *   <Checkbox checked={v} onChange={setV} label="Include tax" />
 *   <Checkbox checked={all} indeterminate={some} onChange={toggleAll} aria-label="Select all" />
 *   <Radio name="freq" value="monthly" checked={f==='monthly'} onChange={setF} label="Monthly" />
 */

const BOX_CLASSES =
  'h-4 w-4 rounded-[2px] cursor-pointer ' +
  'border-[color:var(--color-border)] ' +
  'text-[color:var(--color-action-primary)] ' +
  'focus:ring-2 focus:ring-[color:var(--color-action-primary)] focus:ring-offset-0 ' +
  'disabled:opacity-[var(--opacity-disabled)] disabled:cursor-not-allowed';

export function Checkbox({
  checked,
  indeterminate = false,
  onChange,
  label,
  description,
  disabled = false,
  className,
  inputClassName,
  id,
  stopPropagation = false,
  ...rest
}) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);

  const input = (
    <input
      ref={ref}
      id={id}
      type="checkbox"
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => {
        if (stopPropagation) e.stopPropagation();
        onChange?.(e.target.checked, e);
      }}
      onClick={(e) => { if (stopPropagation) e.stopPropagation(); }}
      className={clsx(BOX_CLASSES, inputClassName)}
      style={{ accentColor: 'var(--color-action-primary)' }}
      {...rest}
    />
  );

  if (!label && !description) {
    return <span data-portica-role="checkbox" className={clsx('inline-flex', className)}>{input}</span>;
  }

  return (
    <label
      data-portica-role="checkbox"
      htmlFor={id}
      className={clsx(
        'inline-flex items-start gap-2 cursor-pointer select-none',
        disabled && 'cursor-not-allowed opacity-[var(--opacity-disabled)]',
        className
      )}
    >
      <span className="flex items-center mt-0.5">{input}</span>
      <span className="min-w-0">
        {label && (
          <span
            className="block text-sm"
            style={{ color: 'var(--color-text-default)' }}
          >
            {label}
          </span>
        )}
        {description && (
          <span
            className="block text-xs"
            style={{ color: 'var(--color-text-muted)' }}
          >
            {description}
          </span>
        )}
      </span>
    </label>
  );
}

export function Radio({
  checked,
  onChange,
  label,
  description,
  value,
  name,
  disabled = false,
  className,
  inputClassName,
  id,
  ...rest
}) {
  const input = (
    <input
      id={id}
      type="radio"
      name={name}
      value={value}
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => onChange?.(value ?? e.target.value, e)}
      className={clsx(
        'h-4 w-4 cursor-pointer',
        'border-[color:var(--color-border)]',
        'text-[color:var(--color-action-primary)]',
        'focus:ring-2 focus:ring-[color:var(--color-action-primary)] focus:ring-offset-0',
        'disabled:opacity-[var(--opacity-disabled)] disabled:cursor-not-allowed',
        inputClassName
      )}
      style={{ accentColor: 'var(--color-action-primary)' }}
      {...rest}
    />
  );

  if (!label && !description) {
    return <span data-portica-role="checkbox" className={clsx('inline-flex', className)}>{input}</span>;
  }

  return (
    <label
      data-portica-role="checkbox"
      htmlFor={id}
      className={clsx(
        'inline-flex items-start gap-2 cursor-pointer select-none',
        disabled && 'cursor-not-allowed opacity-[var(--opacity-disabled)]',
        className
      )}
    >
      <span className="flex items-center mt-0.5">{input}</span>
      <span className="min-w-0">
        {label && (
          <span className="block text-sm" style={{ color: 'var(--color-text-default)' }}>
            {label}
          </span>
        )}
        {description && (
          <span className="block text-xs" style={{ color: 'var(--color-text-muted)' }}>
            {description}
          </span>
        )}
      </span>
    </label>
  );
}

export default Checkbox;
